import type { DocBlock, DocNestedBlock } from "../blocks/blockTypes.js";
import { flattenDocBlocks } from "../blocks/blockFlatten.js";
import type { DocVariableSnapshot } from "../versions/snapshotTypes.js";
import { isValidDocVariableKey } from "./variableKey.js";

export type DocFillInputKind = "text" | "multiline" | "date" | "checkbox";

export type DocFillField = DocVariableSnapshot & {
  inputKind: DocFillInputKind;
};

type FieldSeed = {
  key: string;
  label: string;
  inputKind: DocFillInputKind;
};

function optionalVarKey(raw: string | undefined): string | null {
  const key = raw?.trim() ?? "";
  if (!key || !isValidDocVariableKey(key)) return null;
  return key;
}

function collectBlockFieldSeeds(block: DocBlock | DocNestedBlock): FieldSeed[] {
  const seeds: FieldSeed[] = [];
  switch (block.type) {
    case "text_field":
      if (optionalVarKey(block.variable_key)) {
        seeds.push({
          key: block.variable_key.trim(),
          label: block.label.trim() || block.variable_key.trim(),
          inputKind: "text",
        });
      }
      break;
    case "freeform_field":
      if (optionalVarKey(block.variable_key)) {
        seeds.push({
          key: block.variable_key.trim(),
          label: block.label.trim() || block.variable_key.trim(),
          inputKind: "multiline",
        });
      }
      break;
    case "number_field":
      if (optionalVarKey(block.variable_key)) {
        seeds.push({
          key: block.variable_key.trim(),
          label: block.label.trim() || block.variable_key.trim(),
          inputKind: "text",
        });
      }
      break;
    case "checkbox":
      if (block.fillable && optionalVarKey(block.variable_key)) {
        seeds.push({
          key: block.variable_key.trim(),
          label: block.label.trim() || block.variable_key.trim(),
          inputKind: "checkbox",
        });
      }
      break;
    case "signature_line": {
      const nameKey = optionalVarKey(block.printed_name_variable_key);
      if (nameKey) {
        seeds.push({
          key: nameKey,
          label: block.printed_name_label.trim() || "Printed name",
          inputKind: "text",
        });
      }
      const signatureKey = optionalVarKey(block.signature_variable_key);
      if (signatureKey) {
        seeds.push({
          key: signatureKey,
          label: block.signature_caption.trim() || "Signature",
          inputKind: "text",
        });
      }
      const dateKey = optionalVarKey(block.date_variable_key);
      if (dateKey) {
        seeds.push({
          key: dateKey,
          label: block.date_label.trim() || "Date",
          inputKind: "date",
        });
      }
      break;
    }
    default:
      break;
  }
  return seeds;
}

/** Merge template variables with fill bindings declared on blocks (nested columns included). */
export function buildDocFillFields(
  variables: readonly DocVariableSnapshot[],
  blocks: readonly DocBlock[],
): DocFillField[] {
  const byKey = new Map<string, DocFillField>();

  for (const variable of variables) {
    const key = variable.key.trim();
    if (!key || !isValidDocVariableKey(key)) continue;
    byKey.set(key, {
      key,
      label: variable.label.trim() || key,
      default_value: variable.default_value ?? "",
      required: variable.required,
      inputKind: "text",
    });
  }

  for (const block of flattenDocBlocks(blocks)) {
    for (const seed of collectBlockFieldSeeds(block)) {
      const existing = byKey.get(seed.key);
      if (existing) {
        if (!existing.label || existing.label === existing.key) {
          existing.label = seed.label;
        }
        if (existing.inputKind === "text" && seed.inputKind !== "text") {
          existing.inputKind = seed.inputKind;
        }
        continue;
      }
      byKey.set(seed.key, {
        key: seed.key,
        label: seed.label,
        default_value: "",
        required: false,
        inputKind: seed.inputKind,
      });
    }
  }

  return [...byKey.values()].sort((a, b) => a.label.localeCompare(b.label));
}

export function mergeVariablesFromBlocks(
  blocks: readonly DocBlock[],
  variables: readonly DocVariableSnapshot[],
): DocVariableSnapshot[] {
  return buildDocFillFields(variables, blocks).map(({ inputKind: _k, ...variable }) => variable);
}

export function getUnresolvedRequiredFillFields(
  fields: readonly DocFillField[],
  values: Record<string, string>,
): DocFillField[] {
  return fields.filter((field) => {
    if (!field.required) return false;
    return !(values[field.key] ?? "").trim();
  });
}
